import { m } from 'motion/react';
import { useNavigate, useRouter } from '@tanstack/react-router';

import config from '@/config';
import { AuthCognito } from '@shared/config';
import { AUTH_KEY, IS_SUBSCRIBED_KEY } from '@shared/auth/authentication.hooks';
import { MotionArticleProps } from '@/lib/article-renderer/article';

import styles from '@/lib/article-renderer/core/article.module.css';

/**
 * The article renderer (src/lib/article-renderer) renders this in place of a
 * gated article's components (`components === null`) via its `renderGated`
 * prop -- this is the host app's concrete Cognito-aware implementation, kept
 * out of the library itself since it reaches into this app's `config`,
 * localStorage auth keys and router (improvements/11-article-renderer-
 * library-extraction.md's "renderGated" injection point). Moved verbatim out
 * of virtual-article.tsx's old `SubscriptionRequired` -- same three screens
 * (sign in / subscribe / upgrade), same markup, same behavior.
 */
export function SubscriptionGate(props: Readonly<MotionArticleProps>) {
	const navigate = useNavigate();
	const router = useRouter();
	const basename = `${router.basepath.replace(/\/$/, '')}/callback`;

	const { id, ArticleID, index, className, classNames } = props;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	if (!isAuthenticated) {
		return (
			<m.article
				id={`article-${id}`}
				key={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>Authentication Required</h1>
							<h2>Please sign in to read this article</h2>
							<button
								onClick={() => {
									location.href = redirectUrl;
								}}
							>
								{' '}
								Sign In
							</button>
						</section>
					</div>
				</div>
			</m.article>
		);
	}

	if (!isSubscribed) {
		return (
			<m.article
				id={`article-${id}`}
				key={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>Subscription Required</h1>
							<h2>
								You need a valid subscription to view this
								article
							</h2>
							<button
								type="button"
								onClick={() => {
									navigate({
										to: '/subscriptions',
									});
								}}
							>
								{' '}
								Purchase Subscription
							</button>
						</section>
					</div>
				</div>
			</m.article>
		);
	}

	return (
		<m.article
			id={`article-${id}`}
			data-article-id={ArticleID}
			data-index={index}
			className={className ?? ''}
		>
			<div className={classNames.join(' ')}>
				<div className={styles['subscription-required']}>
					<section>
						<h1>Subscription Upgrade Required</h1>
						<h2>
							Please upgrade your subscription to view this
							article
						</h2>
						<button
							onClick={() => {
								navigate({
									to: '/subscriptions',
								});
							}}
						>
							{' '}
							Upgrade Subscription
						</button>
					</section>
				</div>
			</div>
		</m.article>
	);
}

export default SubscriptionGate;
